import React, { useMemo, useState } from "react";
import { Avatar, Button, Drawer, Dropdown, Layout, Space, Tooltip, Typography, Grid } from "antd";
import {
  BarsOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SunOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Sidebar from "./Sidebar";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { roleLabel } from "../constants/permissions";

const { Header, Content } = Layout;

const AppLayout = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { mode, toggleColorMode } = useTheme();
  const currentRoleLabel = roleLabel(user);

  const userMenuItems = useMemo(
    () => [
      {
        key: "user",
        label: (
          <div style={{ minWidth: 180 }}>
            <Typography.Text strong>{user?.email || "No email"}</Typography.Text>
            <br />
            <Typography.Text type="secondary">{roleLabel(user)}</Typography.Text>
          </div>
        ),
        disabled: true,
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Logout",
      },
    ],
    [user]
  );

  const handleUserMenuClick = async ({ key }) => {
    if (key === "logout") {
      await logout();
    }
  };

  return (
    <Layout className="app-shell">
      {!isMobile && <Sidebar collapsed={collapsed} />}

      <Drawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        placement="left"
        size={320}
        styles={{ body: { padding: 0 } }}
      >
        <Sidebar mobile collapsed={false} onClose={() => setMobileMenuOpen(false)} />
      </Drawer>

      <Layout className="app-main-shell">
        <Header className="app-header-shell">
          <Space size={12} align="center">
            {isMobile ? (
              <Button
                type="text"
                icon={<BarsOutlined />}
                onClick={() => setMobileMenuOpen(true)}
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((value) => !value)}
              />
            )}
            <Typography.Title level={5} style={{ margin: 0 }}>
              {currentRoleLabel}
            </Typography.Title>
          </Space>

          <Space size={8} align="center">
            <Tooltip title={mode === "light" ? "Dark mode" : "Light mode"}>
              <Button
                type="text"
                icon={mode === "light" ? <MoonOutlined /> : <SunOutlined />}
                onClick={toggleColorMode}
              />
            </Tooltip>

            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} trigger={["click"]}>
              <Button type="text" style={{ height: 48, paddingInline: 10 }}>
                <Space size={10}>
                  <Avatar
                    size={32}
                    style={{
                      background: "linear-gradient(135deg, #4b7cf3 0%, #42baf9 100%)",
                    }}
                    icon={<UserOutlined />}
                  />
                  {!isMobile && (
                    <div style={{ textAlign: "left", lineHeight: 1.1 }}>
                      <Typography.Text strong style={{ display: "block" }}>
                        {user?.email || "Account"}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {currentRoleLabel}
                      </Typography.Text>
                    </div>
                  )}
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Header>

        <Content className="app-content">
          <div className="app-page">{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
